import {
  Icon,
  IconButton,
  PageSection,
  Stack,
  TextSubtitle,
  Tooltip,
} from '@pypestream/design-system';
import { FC } from 'react';

import { buildLivePreviewUrl } from '../../utils';
import { sendProStudioEvent } from '../../xstate/app.xstate';

/**
 * "Open Real Preview" — the two ways to reach the channel's REAL running
 * solution (PSTUDIO-3768).
 *
 * The distinction this block exists to draw: the panel on the right of this tab
 * is a static mock (`<PypePreview/>`) that only echoes the branding fields, while
 * these two buttons point at the deployed channel itself. Hence the explicit
 * "live solution" wording on both tooltips.
 */
export const OpenRealPreview: FC<{
  channelId: string;
  env: 'live' | 'sandbox';
}> = ({ channelId, env }) => {
  const url = buildLivePreviewUrl(window.location.origin, env, channelId);

  return (
    <PageSection
      test-id="ps-channel-open-real-preview-section"
      size="small"
      style={{ marginBottom: '10px' }}
    >
      <Stack justifyContent="space-between" alignItems="center" gutter="small">
        <Stack alignItems="center" gutter="2xsmall" nowrap>
          <TextSubtitle test-id="ps-channel-open-real-preview-title">
            Open Real Preview
          </TextSubtitle>
          <Tooltip content="Opens the deployed channel itself — not the mock preview shown on this page.">
            <Icon name="info" size="small" />
          </Tooltip>
        </Stack>
        <Stack alignItems="center" gutter="2xsmall" nowrap>
          <Tooltip content="Copy live solution link">
            <IconButton
              test-id="ps-channel-open-real-preview-copy-button"
              name="copy"
              size="small"
              onClick={() =>
                sendProStudioEvent({
                  type: 'copyToClipboard',
                  value: url,
                  message:
                    'Live solution link has been copied to your clipboard.',
                })
              }
            />
          </Tooltip>
          <Tooltip content="Open live solution in a new tab">
            {/* Rendered as an `<a>` (not an onClick + window.open) so cmd/middle
                click opens it the way any other link would. */}
            <IconButton
              test-id="ps-channel-open-real-preview-open-button"
              name="external-link"
              size="small"
              href={url}
              target="_blank"
            />
          </Tooltip>
        </Stack>
      </Stack>
    </PageSection>
  );
};
